import {
    computed,
    ref,
} from '@vue/composition-api';

import {
    NotificationDescriptor,
    NotificationLevel,
} from './types';

let notificationsStore: ReturnType<typeof createNotificationsStore> | undefined;

let currentId = 0;

type StoreNotification = NotificationDescriptor & {
    id: number;
};

function createNotificationsStore() {
    const defaultNotificationDurationMs = 2_000;

    const currentNotification = ref<StoreNotification>();
    const notificationsQueue = ref<StoreNotification[]>([]);
    let autoCloseTimer: ReturnType<typeof setTimeout> | undefined;

    const visibleNotification = computed(() => currentNotification.value);

    function showNotification(notificationDescriptor: NotificationDescriptor) {
        const nextNotification: StoreNotification = {
            ...notificationDescriptor,
            timeoutMs: notificationDescriptor.timeoutMs ?? defaultNotificationDurationMs,
            // eslint-disable-next-line no-plusplus
            id: currentId++,
        };

        // We need to have a queue, otherwise we might miss notifications.
        // This way, we can animate everything in and out nicely!
        notificationsQueue.value.push(nextNotification);

        if (currentNotification.value == null) {
            showNextNotification();
        }
    }

    function showNextNotification() {
        if (notificationsQueue.value.length > 0) {
            currentNotification.value = notificationsQueue.value[0];
            notificationsQueue.value = notificationsQueue.value.slice(1);
            autoCloseTimer = setTimeout(hideNotification, currentNotification.value.timeoutMs!);
        }
    }

    function hideNotification() {
        currentNotification.value = undefined;
        // Clearing timeout so the next notification won't unexpectedly close
        if (autoCloseTimer) {
            clearTimeout(autoCloseTimer);

            autoCloseTimer = undefined;
        }
        // If we have them queued up, we want to show the next one now!
        showNextNotification();
    }

    function notify(levelOrNotification: NotificationLevel | NotificationDescriptor, message?: string, durationMs?: number): void {
        if (typeof levelOrNotification === 'string') {
            const iconName = (() => {
                switch (levelOrNotification) {
                    case 'info':
                        return 'status-info-filled';
                    case 'warning':
                        return 'status-danger-filled';
                    case 'error':
                        return 'status-warning-filled';
                }
            })();
            const notification: NotificationDescriptor = {
                iconName,
                message: message!,
                timeoutMs: durationMs,
            };

            showNotification(notification);
        } else {
            showNotification(levelOrNotification);
        }
    }

    return {
        visibleNotification,
        showNotification,
        hideNotification,
        notify,
    };
}

export function useNotificationsStore() {
    if (notificationsStore == null) {
        notificationsStore = createNotificationsStore();
    }

    return notificationsStore!;
}
